<template>
  <v-tooltip location="bottom">
    <template #activator="{ props: tooltipProps }">
      <v-icon
        v-bind="tooltipProps"
        color="info"
        size="small"
        class="ml-1"
      >
        mdi-information-outline
      </v-icon>
    </template>
    <div class="pa-3" style="max-width: 800px; line-height: 1.4;">
      <div class="mb-2 font-weight-bold">
        {{ strategyInfo.name }}
      </div>
      <div class="mb-3">
        {{ strategyInfo.description }}
      </div>
      <v-divider class="mb-2" />
      <div>
        <div class="mb-1 font-weight-bold">Example:</div>
        <div class="text-body-2 pa-2 rounded" style="font-family: monospace; white-space: pre-line;">{{ strategyInfo.example }}</div>
      </div>
    </div>
  </v-tooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { MemoMarkingEnum } from '#shared/enums';

const props = defineProps<{ strategy: MemoMarkingEnum }>();

const strategyInfo = computed(() => {
  const strategies = {
    GUESSING_DISCOURAGED: {
      name: 'Guessing Discouraged',
      description: 'Penalizes incorrect answers to discourage random guessing. Wrong answers reduce the overall score.',
      example: 'If a student gets 3 correct and 2 wrong out of 10 questions: Score = (3 - 2) / 10 = 10%'
    },
    PARTIAL_ANSWERS: {
      name: 'Partial Credit',
      description: 'Awards partial points for partially correct answers. Allows for nuanced scoring of complex questions.',
      example: 'A multi-part question worth 5 points where student gets 3 parts correct: Score = 3/5 = 60%'
    },
    PERFECT_ANSWERS: {
      name: 'Perfect Answers Only',
      description: 'Only fully correct answers receive points. No partial credit is awarded for incomplete responses.',
      example: 'Student must select ALL correct options to get points. Selecting 2 out of 3 correct = 0 points'
    },
    SIMPLE_MARKING: {
      name: 'Simple Marking',
      description: 'Standard marking where each correct answer receives full points. Most common marking strategy.',
      example: 'Student answers 7 questions correctly out of 10: Score = 7/10 = 70%'
    }
  };
  
  return strategies[props.strategy] || {
    name: 'Unknown Strategy',
    description: 'No description available for this marking strategy.',
    example: 'No example available.'
  };
});
</script>

<style scoped>
.ml-1 {
  cursor: help;
}
</style>